import React, { useState, useEffect } from 'react';
import { CourseTopBar } from '../course/layout/CourseTopBar';
import { CourseSidebar } from '../course/layout/CourseSidebar';
import CourseDashboard from '../course/pages/CourseDashboard';
import CourseDiscovery from '../course/pages/CourseDiscovery';
import { MyLearning } from '../course/pages/MyLearning';
import { CourseView } from '../course/pages/CourseView';
import LessonDetail from '../course/pages/LessonDetail';
import CreateCourse from '../course/manager/CreateCourse';
import { CourseEditor } from '../course/manager/CourseEditor';
import CreatorAnalytics from '../course/manager/CreatorAnalytics';
import { XPAchievements } from '../course/pages/XPAchievements';
import { CourseCreatorDashboard } from '../course/manager/CourseCreatorDashboard';
import { MySessions } from '../course/pages/MySessions';
import { UserSettings } from '../course/pages/UserSettings';
import { courseService } from '../../services/courseService';
import CourseDetail from '../course/manager/CourseDetail';
import LessonManagement from '../course/manager/LessonManagement';
import LessonEditor from '../course/manager/LessonEditor';
import CreateLesson from '../course/manager/CreateLesson';

export const CourseTemplate = ({ bookId, bookData, user, viewManager, customStyling }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const { viewMode, isAuthenticated, hasManagementAccess } = viewManager || { viewMode: 'public', isAuthenticated: false, hasManagementAccess: false };
  const [activeView, setActiveView] = useState('dashboard');
  const [viewContext, setViewContext] = useState({}); // For passing courseId, lessonId, etc.

  useEffect(() => {
    loadUserProfile();
  }, [user]);

  const loadUserProfile = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      const profile = await courseService.getUserProfile(user.uid, bookId);
      setUserProfile(profile);
    } catch (error) {
      console.error('Error loading user profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const isCreator = () => {
    return bookData?.ownerId === user?.uid || 
           userProfile?.role === 'creator' || 
           userProfile?.role === 'admin';
  };

  const isExpert = () => {
    return userProfile?.role === 'expert' || 
           userProfile?.role === 'creator' || 
           userProfile?.role === 'admin';
  };

  // Navigation handler
  const handleNavigate = (view, context = {}) => {
    setActiveView(view);
    setViewContext(context);
  };

  // Render active view
  const renderActiveView = () => {
    const commonProps = {
      bookId,
      user,
      customStyling,
      userProfile,
      onNavigate: handleNavigate
    };

    switch (activeView) {
      case 'dashboard':
        return <CourseDashboard {...commonProps} />;
      
      case 'discover':
        return <CourseDiscovery {...commonProps} />;
      
      case 'my-learning':
        return <MyLearning {...commonProps} />;
      
      case 'course':
        return <CourseView {...commonProps} courseId={viewContext.courseId} />;
      
      case 'lesson':
        return <LessonDetail {...commonProps} courseId={viewContext.courseId} lessonId={viewContext.lessonId} />;
      
      case 'xp':
        return <XPAchievements {...commonProps} />;
      
      case 'sessions':
        return <MySessions {...commonProps} />;
      
      case 'settings':
        return <UserSettings {...commonProps} />;
      
      // CREATOR VIEWS
      case 'creator':
        if (isCreator()) {
          return <CourseCreatorDashboard {...commonProps} />;
        }
        return <CourseDashboard {...commonProps} />;
      
      case 'creator-new':
        if (isCreator()) {
          return <CreateCourse {...commonProps} />;
        }
        return <CourseDashboard {...commonProps} />;
      
      case 'creator-courses':
      if (isCreator()) {
        return <CourseView {...commonProps} />;
      }
      return <CourseDashboard {...commonProps} />;

      case 'creator-detail':
      if (isCreator()) {
        return <CourseDetail {...commonProps} courseId={viewContext.courseId} />;
      }
      return <CourseDashboard {...commonProps} />;
      
      case 'creator-analytics':
        if (isCreator()) {
          return <CreatorAnalytics {...commonProps} />;
        }
        return <CourseDashboard {...commonProps} />;
      
      case 'creator-edit':
        if (isCreator()) {
        return <CourseEditor {...commonProps} courseId={viewContext.courseId} />;
        }
        return <CourseDashboard {...commonProps} />;
      
      case 'lessons':
        if (isCreator()) {
        return <LessonManagement {...commonProps} courseId={viewContext.courseId} />;
        }
        return <CourseDashboard {...commonProps} />;

      case 'lesson-create':
        if (isCreator()) {
          return <CreateLesson {...commonProps} courseId={viewContext.courseId}/>;
        }
        break;

      case 'lesson-edit':
        if (isCreator()) {
          return <LessonEditor {...commonProps} lessonId={viewContext.lessonId} />;
        }
        break;
      
      default:
        return <CourseDashboard {...commonProps} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {/* Sidebar - Fixed */}
      <CourseSidebar
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        user={user}
        userProfile={userProfile}
        isCreator={isCreator()}
        isExpert={isExpert()}
        bookData={bookData}
        customStyling={customStyling}
        activeView={activeView}
        onNavigate={handleNavigate}
      />

      {/* Main Content - Scrollable */}
      <div className={`flex-1 flex flex-col transition-all duration-300 ${sidebarOpen ? 'lg:ml-80' : 'lg:ml-20'}`}>
        {/* Top Bar - Fixed */}
        <CourseTopBar
          onMenuClick={() => setSidebarOpen(!sidebarOpen)}
          user={user}
          userProfile={userProfile}
          bookData={bookData}
          customStyling={customStyling}
        />

        {/* Content Area - Scrollable */}
        <main className="flex-1 overflow-y-auto">
          {renderActiveView()}
        </main>
      </div>
    </div>
  );
};